import { useCallback, useRef, useState } from 'react';

interface UseDropdownAnimationOptions {
  timeout?: number;
}

interface UseDropdownAnimationReturn {
  isOpen: boolean;
  isMounted: boolean;
  dataState: 'open' | 'closed';
  open: () => void;
  close: () => void;
  toggle: () => void;
  onAnimationEnd: (e: React.AnimationEvent) => void;
}

export function useDropdownAnimation(options?: UseDropdownAnimationOptions): UseDropdownAnimationReturn {
  const timeout = options?.timeout ?? 150;

  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closingGuard = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finishClose = useCallback(() => {
    if (closingGuard.current) return;
    closingGuard.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsClosing(false);
  }, []);

  const open = useCallback(() => {
    closingGuard.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsClosing(false);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    closingGuard.current = false;
    setIsOpen(false);
    setIsClosing(true);
    timeoutRef.current = setTimeout(finishClose, timeout);
  }, [finishClose, timeout]);

  const toggle = useCallback(() => {
    if (isOpen || isClosing) {
      close();
    } else {
      open();
    }
  }, [isOpen, isClosing, open, close]);

  const onAnimationEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === 'exit') finishClose();
  }, [finishClose]);

  return {
    isOpen,
    isMounted: isOpen || isClosing,
    dataState: isClosing ? 'closed' : 'open',
    open,
    close,
    toggle,
    onAnimationEnd,
  };
}
